import type { WrappedCoin } from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { Popover, PopoverContent, PopoverTrigger } from "@screener/uikit/components/popover";
import { cn } from "@screener/uikit/lib/utils";
import { memo } from "react";
import {
  formatUsd,
  lostCauseLabel,
  outcomeLabel,
  sideLabel,
  tickerLabel,
  venueOf,
} from "@/lib/labels";
import { Ago } from "./elapsed";
import { formatPrice } from "./format";
import { OUTCOME_RIBBON_VISIBLE, type OutcomeEntry } from "./stream-state";
import { WrappedMark } from "./wrapped-mark";

interface OutcomeRibbonProps {
  outcomes: readonly OutcomeEntry[];
  /** Перечень обёрнутых монет из приветствия. */
  wrapped: readonly WrappedCoin[];
}

/** Исход ленты; у Lost — с причиной потери через среднюю точку. */
function outcomeText(entry: OutcomeEntry): string {
  const label = outcomeLabel(entry.outcome);
  return entry.lost_cause === undefined ? label : `${label} · ${lostCauseLabel(entry.lost_cause)}`;
}

/**
 * Хвостовая запись ленты. §9 требует от **каждой** записи инструмента, стороны,
 * цены якоря, объёма, исхода и возраста — поэтому хвост несёт тот же состав,
 * что и поповер, а не сокращённый: по укороченной записи нельзя понять, чья
 * это была стена, когда открыты несколько бирж.
 */
function OutcomeText({ entry, wrapped }: { entry: OutcomeEntry; wrapped: readonly WrappedCoin[] }) {
  return (
    <>
      <span className="font-medium text-foreground">{outcomeText(entry)}</span>{" "}
      {venueOf(entry.instrument)}{" "}
      <span className="text-foreground">{tickerLabel(entry.instrument, entry.exchange_base)}</span>{" "}
      <WrappedMark base={entry.instrument.base} wrapped={wrapped} />{" "}
      <span
        className={cn(
          "font-medium tracking-[.06em]",
          entry.side === "bid" ? "text-bid" : "text-ask",
        )}
      >
        {sideLabel(entry.side)}
      </span>{" "}
      {formatPrice(entry.anchor_price)} · {formatUsd(entry.density_usd)} ·{" "}
      <Ago tsMs={entry.ts_ms} />
    </>
  );
}

/**
 * Лента недавних исходов §9: три записи хвостом всегда на виду, полный список
 * глубиной 20 — поповером. Лента эфемерна и живёт только в этой вкладке;
 * журнал находится в библиотеке сайзов §6, и искать по ней здесь нечего.
 */
export const OutcomeRibbon = memo(function OutcomeRibbon({
  outcomes,
  wrapped,
}: OutcomeRibbonProps) {
  const visible = outcomes.slice(0, OUTCOME_RIBBON_VISIBLE);

  return (
    // Строка подвала по макету «Cards Final» (§88 п. 9): кегль и цвет
    // наследуются от подвала, записи — через 16 px, части записи — через 5 px.
    <div className="flex min-w-0 items-center gap-4 overflow-hidden">
      <Popover>
        <PopoverTrigger
          render={
            <Button
              variant="ghost"
              size="sm"
              className="h-auto shrink-0 px-0 py-0 text-[11px] font-medium tracking-[.06em] text-muted-foreground uppercase hover:bg-transparent hover:text-foreground"
            >
              Недавние исходы
            </Button>
          }
        />
        <PopoverContent align="end" className="w-[760px] max-w-[calc(100vw-1rem)] p-0">
          <div className="flex h-8 items-center gap-2 border-b border-border px-3 text-[11px] tracking-wider text-muted-foreground uppercase">
            Недавние исходы
            <span className="ml-auto text-[11px] tracking-normal normal-case tabular-nums">
              {outcomes.length} записей · только эта вкладка
            </span>
          </div>
          <ul className="max-h-[360px] overflow-auto">
            {outcomes.length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-muted-foreground">
                Исходов пока не было.
              </li>
            ) : (
              outcomes.map((entry) => (
                <li
                  key={`${entry.density_id}:${entry.ts_ms}`}
                  className="grid h-7 grid-cols-[148px_minmax(0,1fr)_152px_44px_92px_80px_72px] items-center gap-2 border-b border-border px-3 text-xs text-muted-foreground tabular-nums last:border-0"
                >
                  <span className="font-medium text-foreground">{outcomeText(entry)}</span>
                  <span className="truncate">{venueOf(entry.instrument)}</span>
                  <span className="truncate text-foreground">
                    {tickerLabel(entry.instrument, entry.exchange_base)}{" "}
                    <WrappedMark
                      base={entry.instrument.base}
                      wrapped={wrapped}
                      className="ml-1.5"
                    />
                  </span>
                  <span
                    className={cn(
                      "text-[11px] font-medium tracking-wider uppercase",
                      entry.side === "bid" ? "text-bid" : "text-ask",
                    )}
                  >
                    {sideLabel(entry.side)}
                  </span>
                  <span className="text-right text-foreground">
                    {formatPrice(entry.anchor_price)}
                  </span>
                  <span className="text-right">{formatUsd(entry.density_usd)}</span>
                  <span className="text-right">
                    <Ago tsMs={entry.ts_ms} />
                  </span>
                </li>
              ))
            )}
          </ul>
        </PopoverContent>
      </Popover>

      {visible.length === 0 ? (
        <span className="truncate">Исходов пока не было.</span>
      ) : (
        visible.map((entry) => (
          <span
            key={`${entry.density_id}:${entry.ts_ms}`}
            className="inline-flex min-w-0 items-center gap-[5px] overflow-hidden"
          >
            <OutcomeText entry={entry} wrapped={wrapped} />
          </span>
        ))
      )}
    </div>
  );
});
